// ViewBuildings.tsx
import React, { useEffect, useState } from "react";
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly } from "../../utils/apis.ts";

interface Building {
  id: number;
  building_name: string;
}

const ViewBuildings: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  // Track which building is being edited.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");

  // Fetch saved buildings when the component mounts.
  useEffect(() => {
    fetch(`${baseurly}api/buildings`)
      .then(res => res.json())
      .then((data: Building[]) => setBuildings(data))
      .catch(err => console.error("Error fetching buildings:", err));
  }, []);

  // Start editing a building.
  const handleEdit = (id: number, currentTitle: string) => {
    setEditingId(id);
    setEditTitle(currentTitle);
  };

  // Handle change in the editing input.
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditTitle(e.target.value);
  };

  // Update the building name via a PUT request.
  const handleUpdate = async (id: number) => {
    try {
      const response = await fetch(`${baseurly}api/building/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ building_name: editTitle }),
      });

      if (!response.ok) {
        throw new Error("Failed to update building");
      }

      // Update the local state.
      setBuildings(prev =>
        prev.map(b => (b.id === id ? { ...b, building_name: editTitle } : b))
      );
      setEditingId(null);
      setEditTitle("");
    } catch (error) {
      console.error("Error updating building:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: number) => {
    if (e.key === "Enter") {
      handleUpdate(id);
    }
  };

  const handleBlur = (id: number) => {
    if (editTitle.trim() !== "") {
      handleUpdate(id);
    } else {
      setEditingId(null);
      setEditTitle("");
    }
  };

  return (
    <div className="min-h-screen bg-blue-900 pb-20 flex flex-col items-center">
      {/* Header */}
      <div className="p-4 flex justify-between items-center w-full max-w-5xl">
        <h1 className="text-white text-xl font-bold">Saved Buildings</h1>
      </div>

      {/* Main Content */}
      <div className="bg-white h-screen w-full p-6 rounded-t-3xl flex-1">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-blue-900 text-3xl font-bold mb-4">Buildings List</h2>
          <ul>
            {buildings.map(building => (
              <li
                key={building.id}
                className="flex items-center justify-between border p-4 mb-2 rounded"
              >
                {editingId === building.id ? (
                  <input
                    value={editTitle}
                    onChange={handleTitleChange}
                    onKeyDown={(e) => handleKeyDown(e, building.id)}
                    onBlur={() => handleBlur(building.id)}
                    autoFocus
                    className="flex-1 px-2 py-1 border rounded"
                  />
                ) : (
                  <span className="flex-1">{building.building_name}</span>
                )}
                <button
                  onClick={() => handleEdit(building.id, building.building_name)}
                  className="ml-4 bg-blue-900 text-white px-3 py-1 rounded"
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <BottomNav showBack={true}/>
    </div>
  );
};

export default ViewBuildings;
